import { useDndContext } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import { AddIcon, DeleteIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconButton, Library, Tooltip } from '@treams/treams-ui';

import FeedbackQuestionCard from './FeedbackQuestionCard';

const Ord = styled(Flex)`
	width: 32px;
	height: 32px;
	border-radius: ${(props) => props.theme.tokens.radius.full}px;
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};
	align-items: center;
	justify-content: center;
	color: ${(props) => props.theme.tokens.text.primary};
`;

export default function FeedbackTemplateQuestion(props: {
	index: number;
	question: graphql.FeedbackQuestion;
	canSubjectView?: boolean;
	loading?: boolean;
	onDelete: () => Promise<void>;
	onToggleCanSubjectView?: (canSubjectView: boolean) => void;
}) {
	const { t } = useTranslation();
	const { active } = useDndContext();

	const hasFeedbackQuestionsVisibility = useFeature(
		graphql.FeatureName.FeedbackQuestionsVisibility,
	);

	return (
		<Flex alignItems="center">
			<motion.div
				layoutId={active ? undefined : `ord:${props.question.id}`}
				transition={{ duration: 0.2, ease: 'circOut' }}
			>
				<Ord>{props.index + 1}</Ord>
			</motion.div>
			<Box flexGrow={1} flexShrink={1} ml={2}>
				<Library.Item.Draggable
					embeddedHandle
					item={props.question}
					loading={props.loading}
					buttons={[
						<Tooltip
							content={t('settings.feedbackLibrary.template.questions.remove.button')}
							portal
						>
							<IconButton type="destructive" icon={<DeleteIcon />} onClick={props.onDelete} />
						</Tooltip>,
					]}
					renderCard={(cardProps) => (
						<FeedbackQuestionCard
							{...cardProps}
							canSubjectView={props.canSubjectView}
							hideToggleDisabled={props.loading}
							onToggleCanSubjectView={
								hasFeedbackQuestionsVisibility ? props.onToggleCanSubjectView : undefined
							}
						/>
					)}
					readOnly
				/>
			</Box>
		</Flex>
	);
}

function FeedbackTemplateQuestionSkeleton() {
	return (
		<Flex alignItems="center">
			<Box width={32} />
			<Box flexGrow={1} flexShrink={1} ml={2}>
				<Library.Item.Skeleton />
			</Box>
		</Flex>
	);
}

FeedbackTemplateQuestion.Skeleton = FeedbackTemplateQuestionSkeleton;

const PlaceholderContainer = styled(Flex)`
	border: 1px dashed ${(props) => props.theme.tokens.border.default};
	min-height: 64px;
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
	cursor: pointer;
	background: ${(props) => props.theme.tokens.surface.white};
	transition:
		0.2s ease-in-out border-color,
		0.2s ease-in-out background-color;

	&:hover {
		background: ${(props) => props.theme.tokens.surface.hover};

		&:active {
			background: ${(props) => props.theme.tokens.surface.secondary};
		}
	}
`;

function FeedbackTemplateQuestionPlaceholder() {
	return (
		<PlaceholderContainer alignItems="center" justifyContent="center" ml={80}>
			<IconButton type="secondary" icon={<AddIcon />} />
		</PlaceholderContainer>
	);
}

FeedbackTemplateQuestion.Placeholder = FeedbackTemplateQuestionPlaceholder;
